import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { PENDING_WORKFLOW } from 'citadel-shared/fileManagement.js'
import axios from '../../dataClient/dataClient'
import { FlexEndContainer, PaddedForm, StyledDialog, StyledDialogContent } from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import Button, { SecondaryButton } from '../common/Button'
import ArchiveFileForm from '../files/ArchiveFileForm'
import BulkFileResultList, { FormTitle, Helper, bulkFileSkipMessages, toSkippedRows } from './BulkFileResultList'

export const bulkArchiveFilesDialogStrings = {
  title: count => `Archive ${count} ${count === 1 ? 'file' : 'files'}`,
  resultTitle: 'Archive files',
  helper: 'The selected files move to Removed Files with this reason. They can be restored at any time.',
  resultSummary: (archivedCount, total, failedCount) =>
    `${archivedCount} of ${total} files archived. ${failedCount} ${failedCount === 1 ? 'file' : 'files'} could not be archived:`,
  cancel: 'Cancel',
  close: 'Close',
  error: 'There was an error archiving the files. Please try again.',
  skipMessages: bulkFileSkipMessages,
  skippedFallback: 'File could not be archived'
}

const archiveSchema = yup.object().shape({
  reason: yup.string().required('Reason is required')
})

function BulkArchiveFilesDialog({ open, categoryId, files, onClose = () => {}, onArchived = () => {} }) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState()
  const [result, setResult] = useState()

  const methods = useForm({ resolver: yupResolver(archiveSchema) })
  const { handleSubmit, reset } = methods

  function handleClose() {
    reset({ reason: '' })
    setError(undefined)
    setResult(undefined)
    setIsLoading(false)
    onClose()
  }

  const onError = (errors, e) => console.error('Error', errors, e)

  function onSubmit(data) {
    setIsLoading(true)
    setError(undefined)

    const fileIds = files.map(f => f.id)
    axios
      .post(`/category/${categoryId}/files/archive`, { fileIds, reason: data.reason })
      .then(response => {
        const { archived, skipped } = response.data.data
        onArchived({ archived, skipped })
        if (skipped.length === 0) {
          handleClose()
          return
        }
        setResult({
          archivedCount: archived.length,
          total: fileIds.length,
          skipped: toSkippedRows(skipped, files, bulkArchiveFilesDialogStrings.skippedFallback)
        })
        setIsLoading(false)
      })
      .catch(e => {
        console.error('Failed archiving files', categoryId, e)
        const pendingWorkflow = e.response?.status === 409 && e.response?.data?.code === PENDING_WORKFLOW
        setError((pendingWorkflow && e.response.data.message) || bulkArchiveFilesDialogStrings.error)
        setIsLoading(false)
      })
  }

  return (
    <StyledDialog fullWidth open={open} onClose={isLoading ? undefined : handleClose} keepMounted={false}>
      <StyledDialogContent>
        {result ? (
          <BulkFileResultList
            title={bulkArchiveFilesDialogStrings.resultTitle}
            summary={bulkArchiveFilesDialogStrings.resultSummary(result.archivedCount, result.total, result.skipped.length)}
            skipped={result.skipped}
            closeLabel={bulkArchiveFilesDialogStrings.close}
            onClose={handleClose}
          />
        ) : (
          <FormProvider {...methods}>
            <PaddedForm onSubmit={handleSubmit(onSubmit, onError)}>
              <FormTitle>{bulkArchiveFilesDialogStrings.title(files.length)}</FormTitle>
              <ArchiveFileForm loading={isLoading} />
              <Helper>{bulkArchiveFilesDialogStrings.helper}</Helper>
              {error && <ErrorMessage>{error}</ErrorMessage>}
              <FlexEndContainer>
                <SecondaryButton onClick={handleClose} disabled={isLoading}>
                  {bulkArchiveFilesDialogStrings.cancel}
                </SecondaryButton>
                <Button type="submit" loading={isLoading}>
                  {bulkArchiveFilesDialogStrings.title(files.length)}
                </Button>
              </FlexEndContainer>
            </PaddedForm>
          </FormProvider>
        )}
      </StyledDialogContent>
    </StyledDialog>
  )
}

BulkArchiveFilesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  categoryId: PropTypes.string.isRequired,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      filename: PropTypes.string.isRequired
    })
  ).isRequired,
  onClose: PropTypes.func,
  onArchived: PropTypes.func
}

export default BulkArchiveFilesDialog
